import React, { useEffect, useState } from "react";
import "./FieldsModal.css";
import config from "../../config";
import { MdDeleteForever, MdEdit, MdSave, MdAddCircle, MdCancel, MdArrowBack } from "react-icons/md";

const FieldsModal = ({ token, onClose, isPage = false }) => {
    const [fields, setFields] = useState([]);
    const [newField, setNewField] = useState({ field_name: "", field_type: "str" });
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [editForm, setEditForm] = useState({ field_name: "", field_type: "" });

    const fetchFields = async () => {
        try {
            const res = await fetch(`${config.REACT_APP_API_URL}/fields/user-created`, {
                headers: { Authorization: token },
            });
            if (res.ok) {
                const data = await res.json();
                setFields(data);
            }
        } catch (error) {
            console.error("Failed to fetch fields", error);
        }
    };

    useEffect(() => {
        fetchFields();
    }, []);

    const handleCreate = async () => {
        if (!newField.field_name.trim()) {
            alert("Field name is required.");
            return;
        }

        try {
            await fetch(`${config.REACT_APP_API_URL}/fields`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify([newField]),
            });
            setNewField({ field_name: "", field_type: "str" });
            fetchFields();
        } catch (error) {
            alert("Failed to create field");
        }
    };

    const startEditing = (field) => {
        setEditingFieldId(field.id);
        setEditForm({ field_name: field.field_name, field_type: field.field_type });
    };

    const cancelEditing = () => {
        setEditingFieldId(null);
        setEditForm({ field_name: "", field_type: "" });
    };

    const handleUpdate = async (id) => {
        if (!editForm.field_name.trim()) {
            alert("Field name is required.");
            return;
        }

        try {
            await fetch(`${config.REACT_APP_API_URL}/fields/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify(editForm),
            });
            setEditingFieldId(null);
            fetchFields();
        } catch (error) {
            alert("Failed to update field");
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Are you sure you want to delete this field?")) return;
        
        try {
            await fetch(`${config.REACT_APP_API_URL}/fields/${id}`, {
                method: "DELETE",
                headers: { Authorization: token },
            });
            fetchFields();
        } catch (error) {
            alert("Failed to delete field");
        }
    };

    // --- RENDER HELPERS ---

    const renderTable = () => (
        <div className="table-wrapper fields-modal-table">
            <table className="data-table">
                <thead>
                    <tr>
                        <th style={{ width: '70%' }}>Field Name</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map(field => {
                        const isEditing = editingFieldId === field.id;
                        return (
                            <tr key={field.id}>
                                <td>
                                    {isEditing ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <input
                                                type="text"
                                                className="text-input"
                                                value={editForm.field_name}
                                                onChange={(e) => setEditForm({ ...editForm, field_name: e.target.value })}
                                                autoFocus
                                                placeholder="Field Name"
                                            />
                                            <select
                                                className="select-input"
                                                value={editForm.field_type}
                                                onChange={(e) => setEditForm({ ...editForm, field_type: e.target.value })}
                                            >
                                                <option value="str">String (Text)</option>
                                                <option value="list[str]">List (Dropdown)</option>
                                            </select>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ 
                                                color: 'var(--brand-blue)', 
                                                fontWeight: '600', 
                                                fontSize: '1.1rem' 
                                            }}>
                                                {field.field_name}
                                            </span>
                                            <span style={{ 
                                                color: 'var(--text-secondary)', 
                                                fontSize: '0.85rem',
                                                marginTop: '4px' 
                                            }}>
                                                {field.field_type === 'str' ? 'String' : 'List'}
                                            </span>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <div className="action-buttons-row">
                                        {isEditing ? (
                                            <>
                                                <button 
                                                    className="icon-btn-styled" 
                                                    onClick={() => handleUpdate(field.id)}
                                                    title="Save Changes"
                                                >
                                                    <MdSave size={16} /> <span className="btn-text">Save</span>
                                                </button>
                                                <button 
                                                    className="icon-btn-styled danger" 
                                                    onClick={cancelEditing}
                                                    title="Cancel"
                                                >
                                                    <MdCancel size={16} /> <span className="btn-text">Cancel</span>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    className="icon-btn-styled" 
                                                    onClick={() => startEditing(field)}
                                                    title="Edit Field"
                                                >
                                                    <MdEdit size={16} /> <span className="btn-text">Edit</span>
                                                </button>
                                                <button 
                                                    className="icon-btn-styled danger" 
                                                    onClick={() => handleDelete(field.id)}
                                                    title="Delete Field"
                                                >
                                                    <MdDeleteForever size={16} /> <span className="btn-text">Delete</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {fields.length === 0 && (
                        <tr>
                            <td colSpan="2" style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                                No global fields found. Create one above.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const renderCreationForm = () => (
        <div className="fields-creation-container">
            <div className="input-group" style={{ flex: 2 }}>
                <label className="input-label">Field Name</label>
                <input
                    type="text"
                    className="text-input"
                    value={newField.field_name}
                    onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                    placeholder="e.g. Property Agent Name"
                />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
                <label className="input-label">Field Type</label>
                <select
                    className="select-input"
                    value={newField.field_type}
                    onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                >
                    <option value="str">String (Text)</option>
                    <option value="list[str]">List (Dropdown)</option>
                </select>
            </div>
            <div className="input-group" style={{ flex: 0, minWidth: 'auto' }}>
                 {/* Empty label for alignment */}
                <label className="input-label" style={{visibility: 'hidden'}}>-</label>
                <button 
                    className="primary-button" 
                    onClick={handleCreate}
                    style={{ width: 'auto', whiteSpace: 'nowrap' }}
                >
                    <MdAddCircle size={18} style={{ marginRight: '0.5rem' }}/> 
                    Add Field
                </button>
            </div>
        </div>
    );

    // --- PAGE VIEW ---
    if (isPage) {
        return (
            <div className="customization-content-wrapper">
                 <div className="content-body animate-fade-in">
                    
                    <div className="main-header">
                        <h1>Global Fields</h1>
                        <p className="subtitle">Manage custom fields available across all templates</p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                        <button 
                            className="secondary-button compact-btn" 
                            onClick={onClose} 
                            style={{ width: 'auto', padding: '0 1.2rem' }}
                        >
                            <MdArrowBack style={{ marginRight: '0.5rem' }}/> Back to Customizations
                        </button>
                    </div>

                    {renderCreationForm()}
                    
                    {renderTable()}
                 </div>
            </div>
        );
    }

    // --- MODAL VIEW (Fallback) ---
    return (
        <div className="modal-overlay">
            <div className="modal">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--brand-blue)' }}>User Fields</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <div className="modal-body-scroll">
                    {renderCreationForm()}
                    {renderTable()}
                </div>
            </div>
        </div>
    );
};

export default FieldsModal;